import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import {
  ASSOCIACAO_PREMIACAO_MAX_RECORDS,
  ASSOCIACAO_PREMIACAO_PAGE_SIZE,
  getPremiacaoTabelaById,
  getPremiacaoTabelaVeiculos,
  updatePremiacaoTabela,
  deletePremiacaoTabelaVeiculo
} from '@spark/services/premiacao';
import { fetchPremiacaoPaginatedList } from '../utils/fetchPremiacaoPaginatedList';
import {
  emptyTabelaPremiacaoForm,
  tabelaPremiacaoFormSections,
  tabelaPremiacaoFormStructure,
  validateTabelaPremiacaoForm
} from '../config/tabelaPremiacaoFormConfig';
import { getTabelaVeiculoVinculoColumns } from '../config/tabelaVeiculoVinculoColumns';
import { enriquecerVeiculosVinculo } from '../utils/enriquecerVeiculosVinculo';

const pick = (data, ...keys) => {
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (data?.[key] !== undefined && data?.[key] !== null) {
      return data[key];
    }
  }
  return undefined;
};

const extractValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object' && value.value !== undefined) return value.value;
  return value;
};

const parseNumber = (value) => {
  const rawValue = extractValue(value);
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const parsed = Number(rawValue);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const mapApiToFormValues = (data) => ({
  id: pick(data, 'id', 'Id') ?? '',
  nome: pick(data, 'nome', 'Nome') || '',
  segmento: pick(data, 'segmento', 'Segmento') || '',
  valorInicioFaturamento: pick(data, 'valorInicioFaturamento', 'ValorInicioFaturamento') ?? '',
  valorFinalFaturamento: pick(data, 'valorFinalFaturamento', 'ValorFinalFaturamento') ?? '',
  mediaIdeal: pick(data, 'mediaIdeal', 'MediaIdeal') ?? '',
  mediaExcelente: pick(data, 'mediaExcelente', 'MediaExcelente') ?? '',
  porcentagemDesconto: pick(data, 'porcentagemDesconto', 'PorcentagemDesconto') ?? '',
  valorPremiacao: pick(data, 'valorPremiacao', 'ValorPremiacao') ?? '',
  valorPremiacaoExcelente: pick(data, 'valorPremiacaoExcelente', 'ValorPremiacaoExcelente') ?? '',
  ativo: pick(data, 'ativo', 'Ativo') === true
});

const buildPayload = (values) => ({
  Nome: normalizeText(values.nome) ?? '',
  Segmento: normalizeText(values.segmento),
  ValorInicioFaturamento: parseNumber(values.valorInicioFaturamento) ?? 0,
  ValorFinalFaturamento: parseNumber(values.valorFinalFaturamento) ?? 0,
  MediaIdeal: parseNumber(values.mediaIdeal) ?? 0,
  MediaExcelente: parseNumber(values.mediaExcelente) ?? 0,
  PorcentagemDesconto: parseNumber(values.porcentagemDesconto) ?? 0,
  ValorPremiacao: parseNumber(values.valorPremiacao) ?? 0,
  ValorPremiacaoExcelente: parseNumber(values.valorPremiacaoExcelente) ?? 0,
  Ativo: values.ativo === true
});

const PremiacaoTabelaGerenciarModal = ({
  show,
  onHide,
  tabelaId,
  veiculosLookup,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState(emptyTabelaPremiacaoForm);
  const [veiculos, setVeiculos] = useState([]);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [loadingDeleteVeiculoId, setLoadingDeleteVeiculoId] = useState(null);

  const carregarVeiculos = useCallback(async (id) => {
    if (!id) {
      setVeiculos([]);
      return;
    }

    try {
      setLoadingVeiculos(true);
      const result = await fetchPremiacaoPaginatedList({
        fetchPage: getPremiacaoTabelaVeiculos,
        baseFilters: {
          IdTabelaPremiacao: id,
          Ativo: true
        },
        pageSize: ASSOCIACAO_PREMIACAO_PAGE_SIZE,
        maxRecords: ASSOCIACAO_PREMIACAO_MAX_RECORDS
      });
      const lista = result.registros;
      setVeiculos(enriquecerVeiculosVinculo(
        lista.map((item) => ({
          id: pick(item, 'id', 'Id'),
          numeroFrota: pick(item, 'numeroFrota', 'NumeroFrota') || '',
          ativo: pick(item, 'ativo', 'Ativo') === true,
          data: pick(item, 'data', 'Data'),
          usuarioCriacao: pick(item, 'usuarioCriacao', 'UsuarioCriacao') || ''
        })),
        veiculosLookup
      ));
    } catch (error) {
      console.error('Erro ao carregar veiculos vinculados:', error);
      notification.error('Erro ao carregar veiculos vinculados');
      setVeiculos([]);
    } finally {
      setLoadingVeiculos(false);
    }
  }, [veiculosLookup]);

  const carregarTabela = useCallback(async () => {
    if (!show || !tabelaId) {
      return;
    }

    try {
      setLoading(true);
      const data = await getPremiacaoTabelaById(tabelaId);
      setFormInitialValues(mapApiToFormValues(data));
      await carregarVeiculos(tabelaId);
    } catch (error) {
      console.error('Erro ao carregar tabela:', error);
      notification.error('Erro ao carregar tabela de premiacao');
    } finally {
      setLoading(false);
    }
  }, [show, tabelaId, carregarVeiculos]);

  useEffect(() => {
    if (!show) {
      setFormInitialValues(emptyTabelaPremiacaoForm);
      setVeiculos([]);
      return;
    }
    carregarTabela();
  }, [show, carregarTabela]);

  const handleSubmit = useCallback(async (values) => {
    if (!tabelaId) {
      return;
    }

    try {
      setSaving(true);
      await updatePremiacaoTabela(tabelaId, buildPayload(values));
      notification.success('Tabela de premiacao atualizada com sucesso!');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      notification.error('Erro ao salvar tabela de premiacao');
      console.error('Erro ao salvar tabela:', error);
    } finally {
      setSaving(false);
    }
  }, [onSuccess, tabelaId]);

  const handleDeleteVeiculo = useCallback(async (id) => {
    if (!id || loadingDeleteVeiculoId === id) {
      return;
    }

    try {
      setLoadingDeleteVeiculoId(id);
      await deletePremiacaoTabelaVeiculo(id);
      notification.success('Vínculo removido com sucesso!');
      await carregarVeiculos(tabelaId);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      notification.error('Erro ao remover vinculo');
      console.error('Erro ao remover vinculo:', error);
    } finally {
      setLoadingDeleteVeiculoId(null);
    }
  }, [carregarVeiculos, loadingDeleteVeiculoId, onSuccess, tabelaId]);

  const veiculoColumns = useMemo(
    () => getTabelaVeiculoVinculoColumns(handleDeleteVeiculo, loadingDeleteVeiculoId),
    [handleDeleteVeiculo, loadingDeleteVeiculoId]
  );

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar Tabela de Premiação</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-muted">Carregando...</div>
        ) : (
          <>
            <Form
              onSubmit={handleSubmit}
              initialValues={formInitialValues}
              validate={validateTabelaPremiacaoForm}
            >
              {({ handleSubmit: onFormSubmit }) => (
                <FormContainer onSubmit={onFormSubmit}>
                  <DynamicForm
                    structure={tabelaPremiacaoFormStructure}
                    sections={tabelaPremiacaoFormSections()}
                    readOnly={false}
                  />
                  <FormButtonToolbar className="mt-3">
                    <ButtonWithLoading variant="primary" type="submit" loading={saving}>
                      Salvar
                    </ButtonWithLoading>
                  </FormButtonToolbar>
                </FormContainer>
              )}
            </Form>

            <h6 className="mt-4">Veiculos Vinculados</h6>
            <TanStackTable
              columns={veiculoColumns}
              data={veiculos}
              loading={loadingVeiculos}
              withPagination={false}
              enableRowColors={true}
              enableCopy={true}
              className="mt-2"
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PremiacaoTabelaGerenciarModal;
